import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { formatDate } from './adminUtils.js';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  created_at: string;
  last_active: string | null;
  total_xp: number;
  rank_title: string;
}

export default function AdminUsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<AdminUser[]>('/api/admin/users');
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleBan = async (user: AdminUser) => {
    if (!confirm(`Permanently delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/users/${user.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err) {
      console.error('Failed to ban user:', err);
      alert('Failed to remove user');
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 bg-surface-variant rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-[#141414] thin-border rounded-lg overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-container border-b border-outline-variant">
          <tr>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">Username</th>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">Email</th>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">Joined</th>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">Last active</th>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">XP</th>
            <th className="p-3 font-label-caps text-label-caps text-on-surface-variant uppercase">Rank</th>
            <th className="p-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/30">
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-surface-container-high/50">
              <td className="p-3 font-medium text-on-surface">{u.username}</td>
              <td className="p-3 text-on-surface-variant">{u.email}</td>
              <td className="p-3 text-on-surface-variant">{formatDate(u.created_at)}</td>
              <td className="p-3 text-on-surface-variant">{formatDate(u.last_active)}</td>
              <td className="p-3 text-on-surface">{u.total_xp}</td>
              <td className="p-3 text-error text-xs font-bold uppercase">{u.rank_title}</td>
              <td className="p-3 text-right">
                <button
                  type="button"
                  onClick={() => handleBan(u)}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-400 border border-red-500/40 rounded hover:bg-red-500/10 transition-colors"
                >
                  Ban
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && (
        <p className="p-6 text-on-surface-variant text-center">No users found.</p>
      )}
    </div>
  );
}
